import { Controller } from "@hotwired/stimulus"

interface ChartPoint {
  name: string
  slug: string
  score: number
  cost: number | null
  speed_ms: number | null
  brand_color: string | null
  brand_icon_url: string | null
  released_at?: number | null
  creator?: string | null
}

interface ChartDatum {
  x: number
  y: number
  point: ChartPoint
}

interface LogScale {
  min: number
  max: number
  toX: (v: number) => number
  ticks: number[]
}

interface LinearScale {
  min: number
  max: number
  toY: (v: number) => number
  ticks: number[]
}

const PADDING = { top: 20, right: 20, bottom: 44, left: 56 }
const SELECT_COLOR = "oklch(93% 0.147 126)"  // lime primary from theme — frontier dots/line
const PARETO_COLOR = "oklch(62% 0.17 145)"
const PARETO_LABEL_COLOR = "oklch(40% 0.15 145)"
const FRONTIER_RING = "oklch(30% 0.06 140)"  // dark green ring around lime frontier dots
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

// Timeline hover-to-highlight opacities: the hovered series' line pops, every
// other line/marker fades back so it reads as one trajectory at a time.
const TL_LINE_DEFAULT = "0.6"
const TL_LINE_ACTIVE = "0.95"
const TL_LINE_FADED = "0.08"
const TL_MARKER_FADED = "0.14"

// Brand-icon data-URI cache shared across all instances so multi-panel pages
// (e.g. the homepage's 6 charts) only fetch each icon once, not per controller.
const iconDataUriCache = new Map<string, string>()

// LUMENFALL.AI wordmark SVG path data (original viewBox: 0 0 888 82)
const WATERMARK_VIEWBOX = { w: 888, h: 82 }
const WATERMARK_PATHS = `<g transform="matrix(1.732293,0,0,1.732293,-1410.395027,-570.042415)"><g transform="matrix(50,0,0,50,818,370)"><path d="M0.142,-0L0.142,-0.7L0.312,-0.7L0.312,-0.15L0.713,-0.15L0.713,-0L0.142,-0Z" fill-rule="nonzero"/></g><g transform="matrix(50,0,0,50,856.988,370)"><path d="M0.48,0.006C0.412,0.006 0.352,-0.008 0.3,-0.035C0.248,-0.063 0.208,-0.1 0.179,-0.148C0.15,-0.196 0.135,-0.25 0.135,-0.311L0.135,-0.7L0.307,-0.7L0.307,-0.317C0.307,-0.284 0.314,-0.254 0.329,-0.228C0.345,-0.202 0.365,-0.182 0.392,-0.167C0.418,-0.152 0.447,-0.145 0.48,-0.145C0.514,-0.145 0.544,-0.152 0.571,-0.167C0.598,-0.182 0.619,-0.202 0.635,-0.228C0.65,-0.254 0.658,-0.284 0.658,-0.317L0.658,-0.7L0.824,-0.7L0.824,-0.311C0.824,-0.25 0.809,-0.196 0.78,-0.148C0.751,-0.1 0.711,-0.063 0.66,-0.035C0.608,-0.008 0.548,0.006 0.48,0.006Z" fill-rule="nonzero"/></g><g transform="matrix(50,0,0,50,905.001,370)"><path d="M0.142,-0L0.142,-0.7L0.294,-0.7L0.59,-0.298L0.489,-0.298L0.786,-0.7L0.936,-0.7L0.936,-0L0.771,-0L0.771,-0.224C0.771,-0.281 0.772,-0.334 0.775,-0.383C0.777,-0.431 0.783,-0.48 0.791,-0.529L0.808,-0.475L0.571,-0.17L0.505,-0.17L0.268,-0.475L0.287,-0.529C0.294,-0.481 0.3,-0.433 0.303,-0.385C0.306,-0.336 0.307,-0.283 0.307,-0.224L0.307,-0L0.142,-0Z" fill-rule="nonzero"/></g><g transform="matrix(50,0,0,50,958.89,370)"><path d="M0.142,-0L0.142,-0.7L0.719,-0.7L0.719,-0.555L0.306,-0.555L0.306,-0.145L0.729,-0.145L0.729,-0L0.142,-0ZM0.228,-0.287L0.228,-0.425L0.674,-0.425L0.674,-0.287L0.228,-0.287Z" fill-rule="nonzero"/></g><g transform="matrix(50,0,0,50,1001.04,370)"><path d="M0.142,-0L0.142,-0.7L0.3,-0.7L0.757,-0.2L0.73,-0.205C0.727,-0.229 0.724,-0.252 0.722,-0.275C0.719,-0.297 0.717,-0.318 0.716,-0.338C0.715,-0.359 0.714,-0.379 0.713,-0.399C0.712,-0.419 0.711,-0.44 0.71,-0.461C0.71,-0.482 0.71,-0.504 0.71,-0.527L0.71,-0.7L0.875,-0.7L0.875,-0L0.715,-0L0.242,-0.516L0.287,-0.509C0.289,-0.491 0.292,-0.474 0.293,-0.458C0.295,-0.442 0.297,-0.426 0.299,-0.409C0.301,-0.393 0.302,-0.376 0.303,-0.358C0.304,-0.34 0.305,-0.321 0.306,-0.301C0.307,-0.281 0.307,-0.258 0.307,-0.234L0.307,-0L0.142,-0Z" fill-rule="nonzero"/></g><g transform="matrix(50,0,0,50,1051.879,370)"><path d="M0.142,-0L0.142,-0.7L0.312,-0.7L0.312,-0L0.142,-0ZM0.213,-0.262L0.213,-0.404L0.66,-0.404L0.66,-0.262L0.213,-0.262ZM0.222,-0.55L0.222,-0.7L0.701,-0.7L0.701,-0.55L0.222,-0.55Z" fill-rule="nonzero"/></g><g transform="matrix(50,0,0,50,1089.183,370)"><path d="M0.042,-0L0.364,-0.7L0.519,-0.7L0.839,-0L0.664,-0L0.484,-0.393C0.478,-0.408 0.472,-0.422 0.466,-0.437C0.46,-0.451 0.454,-0.465 0.449,-0.478C0.444,-0.492 0.438,-0.506 0.434,-0.52C0.429,-0.534 0.425,-0.548 0.421,-0.561L0.457,-0.562C0.452,-0.547 0.448,-0.533 0.443,-0.519C0.438,-0.505 0.433,-0.491 0.427,-0.477C0.422,-0.463 0.416,-0.449 0.41,-0.435C0.404,-0.422 0.398,-0.407 0.392,-0.392L0.212,-0L0.042,-0ZM0.192,-0.128L0.247,-0.257L0.631,-0.257L0.665,-0.128L0.192,-0.128Z" fill-rule="nonzero"/></g><g transform="matrix(50,0,0,50,1133.246,370)"><path d="M0.142,-0L0.142,-0.7L0.312,-0.7L0.312,-0.15L0.713,-0.15L0.713,-0L0.142,-0Z" fill-rule="nonzero"/></g><g transform="matrix(50,0,0,50,1173.234,370)"><path d="M0.142,-0L0.142,-0.7L0.312,-0.7L0.312,-0.15L0.713,-0.15L0.713,-0L0.142,-0Z" fill-rule="nonzero"/></g><g transform="matrix(50,0,0,50,1213.222,370)"><path d="M0.23,0.011C0.195,0.011 0.168,0.003 0.15,-0.013C0.131,-0.029 0.122,-0.05 0.122,-0.078C0.122,-0.103 0.131,-0.124 0.15,-0.141C0.169,-0.159 0.195,-0.168 0.23,-0.168C0.266,-0.168 0.294,-0.16 0.312,-0.144C0.33,-0.128 0.34,-0.106 0.34,-0.078C0.34,-0.054 0.33,-0.033 0.311,-0.016C0.292,0.002 0.265,0.011 0.23,0.011Z" fill-rule="nonzero"/></g><g transform="matrix(50,0,0,50,1236.307,370)"><path d="M0.042,-0L0.364,-0.7L0.519,-0.7L0.839,-0L0.664,-0L0.484,-0.393C0.478,-0.408 0.472,-0.422 0.466,-0.437C0.46,-0.451 0.454,-0.465 0.449,-0.478C0.444,-0.492 0.438,-0.506 0.434,-0.52C0.429,-0.534 0.425,-0.548 0.421,-0.561L0.457,-0.562C0.452,-0.547 0.448,-0.533 0.443,-0.519C0.438,-0.505 0.433,-0.491 0.427,-0.477C0.422,-0.463 0.416,-0.449 0.41,-0.435C0.404,-0.422 0.398,-0.407 0.392,-0.392L0.212,-0L0.042,-0ZM0.192,-0.128L0.247,-0.257L0.631,-0.257L0.665,-0.128L0.192,-0.128Z" fill-rule="nonzero"/></g><g transform="matrix(50,0,0,50,1280.37,370)"><path d="M0.15,-0L0.15,-0.147L0.312,-0.147L0.312,-0.553L0.15,-0.553L0.15,-0.7L0.646,-0.7L0.646,-0.553L0.482,-0.553L0.482,-0.147L0.646,-0.147L0.646,-0L0.15,-0Z" fill-rule="nonzero"/></g></g>`

export default class extends Controller {
  static targets = ["chart"]
  static values = {
    points: Array,
    xKey: { type: String, default: "cost" },
    yKey: { type: String, default: "score" },
    xLabel: { type: String, default: "Cost ($/image)" },
    yLabel: { type: String, default: "Score" },
    xFormat: { type: String, default: "currency" },
    yFormat: { type: String, default: "number" },
    // "log" (default, cost/speed charts) or "time" (linear date axis).
    xScale: { type: String, default: "log" },
    // "" (default) or "creator" — draw one step line per creator instead of the
    // global Pareto frontier (release-date-vs-quality timeline).
    connectBy: { type: String, default: "" },
    // Static per-point labels. On by default (cost/speed charts label the
    // frontier); the timeline turns this off and relies on the legend + hover.
    showLabels: { type: Boolean, default: true },
    // "lines" (default grid) or "dots" (dotted-paper background, used by the timeline).
    gridStyle: { type: String, default: "lines" },
    // When set, every connect-by line uses this stroke instead of each group's
    // brand color (single-creator timeline: dots are per-model, line is the creator's).
    lineColor: { type: String, default: "" },
    // Slightly smaller axis tick + axis-label text (used by the timeline).
    compactAxes: { type: Boolean, default: false },
    highlight: String,
    chartTitle: String,
    exportTitle: String,
    showPareto: { type: Boolean, default: false },
    showParetoSummary: { type: Boolean, default: true },
    xBestDirection: { type: String, default: "low" },
    yBestDirection: { type: String, default: "high" },
    svgWidth: { type: Number, default: 560 },
    svgHeight: { type: Number, default: 320 },
    pointRadius: { type: Number, default: 5 },
    // "" (default, disabled), "creator", or "slug": enables hover-to-highlight on
    // the timeline. Hovering a line, marker, or legend row isolates that series
    // and fades the rest. "creator" groups by lab (all-creators view); "slug" by
    // model (single-creator view: one line, per-model markers/legend).
    seriesBy: { type: String, default: "" },
  }

  declare chartTarget: HTMLElement
  declare hasChartTarget: boolean
  declare pointsValue: ChartPoint[]
  declare xKeyValue: string
  declare yKeyValue: string
  declare xLabelValue: string
  declare yLabelValue: string
  declare xFormatValue: string
  declare yFormatValue: string
  declare xScaleValue: string
  declare connectByValue: string
  declare showLabelsValue: boolean
  declare gridStyleValue: string
  declare lineColorValue: string
  declare compactAxesValue: boolean
  declare highlightValue: string
  declare hasHighlightValue: boolean
  declare chartTitleValue: string
  declare hasChartTitleValue: boolean
  declare exportTitleValue: string
  declare hasExportTitleValue: boolean
  declare showParetoValue: boolean
  declare showParetoSummaryValue: boolean
  declare xBestDirectionValue: string
  declare yBestDirectionValue: string
  declare svgWidthValue: number
  declare svgHeightValue: number
  declare pointRadiusValue: number
  declare seriesByValue: string

  private svg!: SVGSVGElement
  private tooltip!: HTMLDivElement
  private selectedCircle: SVGCircleElement | null = null
  private selectedLabel: SVGTextElement | null = null
  // The clicked (pinned) point: its tooltip stays open after the mouse leaves,
  // until the point is clicked again or a different one is clicked.
  private pinnedPoint: { d: ChartDatum; cx: number; cy: number } | null = null
  private labelMap = new Map<string, SVGTextElement>()
  private iconMap = new Map<SVGCircleElement, SVGElement>()
  private colors!: { grid: string; muted: string; text: string; label: string; dot: string; leader: string; pareto: string; paretoLabel: string; subdued: string; dotMuted: string; frontierRing: string; dotGrid: string }
  private width = 560
  private height = 320
  private io: IntersectionObserver | null = null
  private rendered = false
  // Hover-to-highlight (timeline only): collected during render(), consumed by
  // enterHighlight()/clearHighlight() when a line/marker/legend row is hovered.
  private hlMarkers: { el: SVGElement; series: string }[] = []
  private hlLines: { el: SVGPathElement; members: Set<string> }[] = []
  private hlLegend: { el: HTMLElement; series: string }[] = []
  private hlClearRaf = 0

  connect() {
    this.width = this.svgWidthValue
    this.height = this.svgHeightValue
    // IO-gate the first render so offscreen / hidden-tab panels don't render
    // (and, on export, prefetch) until they're actually visible. Fall back to
    // an immediate render if IntersectionObserver is unavailable.
    if (typeof IntersectionObserver === "undefined") {
      this.renderOnce()
      return
    }
    this.io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.renderOnce()
          break
        }
      }
    }, { rootMargin: "200px" })
    this.io.observe(this.element)
  }

  disconnect() {
    this.cancelClear()
    if (this.io) {
      this.io.disconnect()
      this.io = null
    }
  }

  /** Render exactly once per controller, then stop observing. */
  private renderOnce() {
    if (this.rendered) return
    this.rendered = true
    if (this.io) {
      this.io.disconnect()
      this.io = null
    }
    this.render()
  }

  /** Pre-fetch brand icons and cache as data URIs so the SVG embeds them inline.
   *  Works when same-origin; silently skips cross-origin URLs (CORS).
   *  Export falls back to colored dots for icons that couldn't be cached.
   *  Called lazily at export time (not on load) to avoid CORS console noise. */
  private async prefetchIcons() {
    const urls = [...new Set(
      this.pointsValue
        .map(p => p.brand_icon_url)
        .filter((u): u is string => !!u)
        .filter(u => !iconDataUriCache.has(u))
    )]
    if (urls.length === 0) return
    await Promise.all(urls.map(async (url) => {
      try {
        const res = await fetch(url)
        if (!res.ok) return
        const blob = await res.blob()
        const dataUri = await new Promise<string>((resolve) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result as string)
          reader.readAsDataURL(blob)
        })
        iconDataUriCache.set(url, dataUri)
      } catch { /* cache miss — export will fall back to colored dots */ }
    }))
  }

  private get plotWidth() { return this.width - PADDING.left - PADDING.right }
  private get plotHeight() { return this.height - PADDING.top - PADDING.bottom }

  private get chartContainer(): HTMLElement {
    return this.hasChartTarget ? this.chartTarget : this.element as HTMLElement
  }

  private render() {
    const data = this.filteredData()
    if (data.length === 0) return

    this.hlMarkers = []
    this.hlLines = []
    this.hlLegend = []

    // Create container
    const container = document.createElement("div")
    container.className = "relative"

    // SVG
    this.svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    this.svg.setAttribute("viewBox", `0 0 ${this.width} ${this.height}`)
    // `block` removes the inline-SVG descender gap so the rendered height matches
    // the container's reserved aspect-ratio exactly (no late layout shift).
    this.svg.setAttribute("class", "w-full h-auto block")
    this.svg.style.overflow = "visible"

    // Tooltip
    this.tooltip = document.createElement("div")
    this.tooltip.className = "absolute pointer-events-none opacity-0 transition-opacity duration-150 bg-base-100 border border-base-200 rounded-lg shadow-lg px-3 py-2 text-xs z-50 whitespace-nowrap text-base-content"
    this.tooltip.style.transform = "translate(-50%, -100%)"

    // Resolve theme colors using color-mix (matches daisyUI pattern)
    const bc = "var(--color-base-content)"
    this.colors = {
      grid:   `color-mix(in oklch, ${bc} 10%, transparent)`,
      muted:  `color-mix(in oklch, ${bc} 40%, transparent)`,
      text:   `color-mix(in oklch, ${bc} 65%, transparent)`,
      label:  bc,
      dot:    bc,
      leader: `color-mix(in oklch, ${bc} 25%, transparent)`,
      pareto: PARETO_COLOR,
      paretoLabel: PARETO_LABEL_COLOR,
      subdued: `color-mix(in oklch, ${bc} 24%, transparent)`,
      dotMuted: `color-mix(in oklch, ${bc} 34%, transparent)`,
      frontierRing: FRONTIER_RING,
      dotGrid: `color-mix(in oklch, ${bc} 15%, transparent)`,
    }

    const xVals = data.map(d => d.x)
    const yVals = data.map(d => d.y)

    const xScale = this.xScaleValue === "time" ? this.buildTimeScale(xVals) : this.buildLogScale(xVals)
    const yScale = this.buildLinearScale(yVals)
    const byCreator = this.connectByValue === "creator"
    const pareto = this.showParetoValue && !byCreator ? this.computePareto(data) : []
    const paretoSlugs = new Set(pareto.map(d => d.point.slug))

    // No white plot-area fill — the calm warm base-100 of the card shows
    // through, which reads quieter than a brightened panel. No shaded frontier
    // region either: the frontier line + dots carry it (best practice; a filled
    // area reads misleadingly as "this whole zone is good").

    // Draw layers in order: grid → watermark → axes → Pareto line → labels → points
    this.drawGrid(xScale, yScale)
    this.drawWatermark()
    this.drawAxes(xScale, yScale)
    if (byCreator) {
      this.drawCreatorLines(data, xScale, yScale)
    } else if (pareto.length >= 2) {
      this.drawParetoLine(pareto, xScale, yScale)
    }
    // The dense all-creators timeline turns labels off (legend + hover instead);
    // single-creator and cost/speed charts keep them on.
    if (this.showLabelsValue) this.drawLabels(data, xScale, yScale, paretoSlugs)
    this.drawPoints(data, xScale, yScale, paretoSlugs)

    container.appendChild(this.svg)
    container.appendChild(this.tooltip)
    if (this.showParetoValue && this.showParetoSummaryValue && pareto.length > 0) {
      container.appendChild(this.buildParetoSummary(pareto))
    }
    this.chartContainer.innerHTML = ""
    this.chartContainer.appendChild(container)

    if (this.seriesByValue) this.setupLegendHighlight()
  }

  private filteredData(): ChartDatum[] {
    return this.pointsValue
      .filter(p => {
        const x = (p as any)[this.xKeyValue]
        const y = (p as any)[this.yKeyValue]
        return x != null && x > 0 && y != null
      })
      .map(p => ({
        x: (p as any)[this.xKeyValue] as number,
        y: (p as any)[this.yKeyValue] as number,
        point: p,
      }))
  }

  private buildLogScale(vals: number[]): LogScale {
    const logMin = Math.floor(Math.log10(Math.min(...vals)))
    const logMax = Math.ceil(Math.log10(Math.max(...vals)))
    const min = Math.pow(10, logMin)
    const max = Math.pow(10, logMax)
    const logRange = logMax - logMin || 1

    const ticks: number[] = []
    for (let e = logMin; e <= logMax; e++) {
      ticks.push(Math.pow(10, e))
    }

    return {
      min,
      max,
      toX: (v: number) => PADDING.left + ((Math.log10(v) - logMin) / logRange) * this.plotWidth,
      ticks,
    }
  }

  // Linear date axis (epoch-ms values). Structurally a LogScale (min/max/toX/
  // ticks) so the existing draw* methods work unchanged.
  private buildTimeScale(vals: number[]): LogScale {
    const dataMin = Math.min(...vals)
    const dataMax = Math.max(...vals)
    const span = dataMax - dataMin || 86_400_000
    const pad = span * 0.04
    const min = dataMin - pad
    const max = dataMax + pad
    const range = max - min || 1

    return {
      min,
      max,
      toX: (v: number) => PADDING.left + ((v - min) / range) * this.plotWidth,
      ticks: this.buildDateTicks(min, max),
    }
  }

  // Year ticks for multi-year spans, quarter ticks for shorter ones.
  private buildDateTicks(minMs: number, maxMs: number): number[] {
    const spanDays = (maxMs - minMs) / 86_400_000
    const startY = new Date(minMs).getUTCFullYear()
    const endY = new Date(maxMs).getUTCFullYear()
    const ticks: number[] = []

    if (spanDays > 420) {
      for (let y = startY; y <= endY + 1; y++) {
        const t = Date.UTC(y, 0, 1)
        if (t >= minMs && t <= maxMs) ticks.push(t)
      }
    }
    if (ticks.length < 3) {
      ticks.length = 0
      for (let y = startY; y <= endY + 1; y++) {
        for (const m of [0, 3, 6, 9]) {
          const t = Date.UTC(y, m, 1)
          if (t >= minMs && t <= maxMs) ticks.push(t)
        }
      }
    }
    if (ticks.length < 2) ticks.push(minMs, maxMs)
    return ticks
  }

  private buildLinearScale(vals: number[]): LinearScale {
    const dataMin = Math.min(...vals)
    const dataMax = Math.max(...vals)
    const rawRange = dataMax - dataMin
    const range = rawRange || Math.max(Math.abs(dataMax) * 0.02, 1)
    const padBottom = rawRange === 0 ? range : range * 0.08
    // Extra headroom on top so the highest-scoring models aren't jammed against
    // the frame, leaving room for their labels to sit above the dot/logo.
    const padTop = rawRange === 0 ? range : range * 0.16
    const min = dataMin - padBottom
    const max = dataMax + padTop

    const ticks = this.niceLinearTicks(min, max, 5)

    return {
      min,
      max,
      toY: (v: number) => PADDING.top + this.plotHeight - ((v - min) / (max - min)) * this.plotHeight,
      ticks,
    }
  }

  private niceLinearTicks(min: number, max: number, count: number): number[] {
    const range = max - min
    const rough = range / count
    const pow = Math.pow(10, Math.floor(Math.log10(rough)))
    const nice = rough / pow >= 5 ? 5 * pow : rough / pow >= 2 ? 2 * pow : pow

    const ticks: number[] = []
    let tick = Math.ceil(min / nice) * nice
    while (tick <= max) {
      ticks.push(tick)
      tick += nice
    }
    return ticks
  }

  private computePareto(data: ChartDatum[]): ChartDatum[] {
    const xHighIsBetter = this.xBestDirectionValue === "high"
    const yHighIsBetter = this.yBestDirectionValue === "high"

    const frontier = data.filter(candidate => {
      return !data.some(other => {
        if (other === candidate) return false

        const xBetterOrEqual = xHighIsBetter ? other.x >= candidate.x : other.x <= candidate.x
        const yBetterOrEqual = yHighIsBetter ? other.y >= candidate.y : other.y <= candidate.y
        const xStrictlyBetter = xHighIsBetter ? other.x > candidate.x : other.x < candidate.x
        const yStrictlyBetter = yHighIsBetter ? other.y > candidate.y : other.y < candidate.y

        return xBetterOrEqual && yBetterOrEqual && (xStrictlyBetter || yStrictlyBetter)
      })
    })

    return frontier.sort((a, b) => a.x - b.x)
  }

  private drawParetoLine(frontier: ChartDatum[], xScale: LogScale, yScale: LinearScale) {
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
    const d = frontier.map((point, index) => {
      const x = xScale.toX(point.x)
      const y = yScale.toY(point.y)
      return `${index === 0 ? "M" : "L"}${x},${y}`
    }).join("")

    path.setAttribute("d", d)
    path.setAttribute("fill", "none")
    path.style.stroke = SELECT_COLOR
    path.style.strokeWidth = "2.25"
    path.setAttribute("stroke-linecap", "round")
    path.setAttribute("stroke-linejoin", "round")
    path.setAttribute("data-pareto-line", "true")
    this.svg.appendChild(path)
  }

  // One step line per creator, connecting that creator's releases oldest→newest
  // and holding each level until the next release (step-before). Colored by the
  // creator's brand color. Drawn under the dots.
  private drawCreatorLines(data: ChartDatum[], xScale: LogScale, yScale: LinearScale) {
    const groups = new Map<string, ChartDatum[]>()
    for (const d of data) {
      const key = d.point.creator || "Other"
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(d)
    }

    for (const pts of groups.values()) {
      if (pts.length < 2) continue
      const sorted = [...pts].sort((a, b) => a.x - b.x)
      const color = this.lineColorValue || sorted[0].point.brand_color || this.colors.dotMuted

      let d = ""
      sorted.forEach((pt, i) => {
        const x = xScale.toX(pt.x)
        const y = yScale.toY(pt.y)
        if (i === 0) {
          d += `M${x},${y}`
        } else {
          const prevY = yScale.toY(sorted[i - 1].y)
          d += `L${x},${prevY}L${x},${y}`
        }
      })

      const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
      path.setAttribute("d", d)
      path.setAttribute("fill", "none")
      path.style.stroke = color
      path.style.strokeWidth = "1"
      path.style.opacity = TL_LINE_DEFAULT
      path.setAttribute("stroke-linecap", "round")
      path.setAttribute("stroke-linejoin", "round")
      path.dataset.creatorLine = "1"
      this.svg.appendChild(path)

      // Register the line as a highlight target so legend hover can fade it. The
      // line itself isn't a hover trigger — only the legend is.
      if (this.seriesByValue) {
        path.style.transition = "opacity 0.15s ease"
        this.hlLines.push({ el: path, members: new Set(sorted.map(p => this.seriesOf(p.point))) })
      }
    }
  }

  // The grouping key a marker/line/legend row shares: lab name (all-creators
  // view) or model slug (single-creator view).
  private seriesOf(point: ChartPoint): string {
    return this.seriesByValue === "slug" ? point.slug : (point.creator || "Other")
  }

  private buildParetoSummary(frontier: ChartDatum[]): HTMLDivElement {
    const sorted = [...frontier].sort((a, b) => (
      this.yBestDirectionValue === "high" ? b.y - a.y : a.y - b.y
    ))
    const summary = document.createElement("div")
    summary.className = "mt-3 border-t border-base-content/10 pt-3"

    const header = document.createElement("div")
    header.className = "flex items-center justify-between gap-3 text-[11px] uppercase tracking-wide text-base-content/45 mb-2"
    header.innerHTML = `
      <span class="inline-flex items-center gap-1.5">
        <span>Pareto frontier</span>
        <span class="tooltip tooltip-top normal-case tracking-normal" data-tip="These are the models that give the best score available at their price. A model drops off the line when another model is both cheaper and better.">
          <span class="inline-flex size-4 items-center justify-center rounded-full border border-base-content/20 text-[10px] font-semibold text-base-content/55">i</span>
        </span>
      </span>
      <span>${frontier.length} optimal</span>
    `
    summary.appendChild(header)

    const list = document.createElement("div")
    list.className = "grid grid-cols-1 sm:grid-cols-2 gap-1.5"
    for (const d of sorted.slice(0, 6)) {
      const item = document.createElement("div")
      item.className = "flex items-center justify-between gap-3 rounded-md bg-base-content/[0.035] px-2.5 py-1.5 text-xs"

      const name = document.createElement("span")
      name.className = "min-w-0 truncate font-medium text-base-content/75"
      name.textContent = d.point.name

      const values = document.createElement("span")
      values.className = "shrink-0 tabular-nums text-base-content/45"
      values.textContent = `${this.formatVal(d.y, this.yFormatValue)} · ${this.formatVal(d.x, this.xFormatValue)}`

      item.appendChild(name)
      item.appendChild(values)
      list.appendChild(item)
    }
    summary.appendChild(list)

    return summary
  }

  private drawGrid(xScale: LogScale, yScale: LinearScale) {
    if (this.gridStyleValue === "dots") {
      this.drawDottedGrid()
      return
    }

    // Horizontal grid lines
    for (const tick of yScale.ticks) {
      const y = yScale.toY(tick)
      this.line(PADDING.left, y, PADDING.left + this.plotWidth, y, this.colors.grid, 1)
    }

    // Vertical grid lines
    for (const tick of xScale.ticks) {
      const x = xScale.toX(tick)
      this.line(x, PADDING.top, x, PADDING.top + this.plotHeight, this.colors.grid, 1)
    }
  }

  // Dotted-paper background: a faint dot matrix across the plot area instead of
  // grid lines (used by the timeline chart).
  private drawDottedGrid() {
    const left = PADDING.left
    const top = PADDING.top
    const right = PADDING.left + this.plotWidth
    const bottom = PADDING.top + this.plotHeight
    const step = 26

    for (let x = left; x <= right + 0.5; x += step) {
      for (let y = top; y <= bottom + 0.5; y += step) {
        const dot = document.createElementNS("http://www.w3.org/2000/svg", "circle")
        dot.setAttribute("cx", String(x))
        dot.setAttribute("cy", String(y))
        dot.setAttribute("r", "1")
        dot.style.fill = this.colors.dotGrid
        dot.style.pointerEvents = "none"
        this.svg.appendChild(dot)
      }
    }
  }

  private drawWatermark(opacityOverride?: number) {
    const opacity = opacityOverride ?? 0.06
    const g = document.createElementNS("http://www.w3.org/2000/svg", "g")
    g.setAttribute("data-watermark", "true")

    // Scale the wordmark to ~60% of the plot width, centered in the plot area
    const targetW = this.plotWidth * 0.6
    const scale = targetW / WATERMARK_VIEWBOX.w
    const targetH = WATERMARK_VIEWBOX.h * scale
    const cx = PADDING.left + this.plotWidth / 2
    const cy = PADDING.top + this.plotHeight / 2
    const tx = cx - targetW / 2
    const ty = cy - targetH / 2

    g.setAttribute("transform", `translate(${tx}, ${ty}) scale(${scale})`)
    g.setAttribute("opacity", String(opacity))
    g.style.fill = "var(--color-base-content)"
    g.setAttribute("fill-rule", "evenodd")
    g.setAttribute("clip-rule", "evenodd")
    g.setAttribute("stroke-linejoin", "round")
    g.setAttribute("stroke-miterlimit", "2")
    g.innerHTML = WATERMARK_PATHS

    this.svg.appendChild(g)
  }

  private drawAxes(xScale: LogScale, yScale: LinearScale) {
    const tickSize = this.compactAxesValue ? "8.5px" : "10px"
    const labelSize = this.compactAxesValue ? "9.5px" : "11px"

    // X axis ticks
    for (const tick of xScale.ticks) {
      const x = xScale.toX(tick)
      this.text(x, PADDING.top + this.plotHeight + 16, this.formatVal(tick, this.xFormatValue), this.colors.muted, "middle", tickSize)
    }

    // Y axis ticks
    for (const tick of yScale.ticks) {
      const y = yScale.toY(tick)
      this.text(PADDING.left - 8, y + 3, this.formatVal(tick, this.yFormatValue), this.colors.muted, "end", tickSize)
    }

    // Axis labels
    this.text(PADDING.left + this.plotWidth / 2, this.height - 4, this.xLabelValue, this.colors.text, "middle", labelSize)

    const labelEl = this.text(0, 0, this.yLabelValue, this.colors.text, "middle", labelSize)
    labelEl.setAttribute("transform", `translate(14, ${PADDING.top + this.plotHeight / 2}) rotate(-90)`)
  }

  private drawPoints(data: ChartDatum[], xScale: LogScale, yScale: LinearScale, paretoSlugs: Set<string>) {
    const highlightSlug = this.hasHighlightValue ? this.highlightValue : null
    const byCreator = this.connectByValue === "creator"

    for (const d of data) {
      const cx = xScale.toX(d.x)
      const cy = yScale.toY(d.y)

      // Timeline markers are brand-colored SQUARES with a simple pinned-tooltip
      // interaction (no frontier highlight / logo / resize machinery).
      if (byCreator) {
        this.drawTimelineMarker(d, cx, cy)
        continue
      }

      const isHighlighted = highlightSlug && d.point.slug === highlightSlug
      const isDimmed = highlightSlug && !isHighlighted
      const isPareto = paretoSlugs.has(d.point.slug)
      const onFrontier = isPareto || !!isHighlighted
      // Timeline dots are brand-colored markers (no logos — too dense across 40+
      // points); the per-creator legend carries identity.
      const hasIcon = !byCreator && !!d.point.brand_icon_url

      // Models with a creator logo show the logo; everyone else is a dot —
      // lime (with a dark ring) on the frontier, calm muted grey otherwise.
      const fill = byCreator
        ? (d.point.brand_color || this.colors.dotMuted)
        : onFrontier ? SELECT_COLOR : this.colors.dotMuted
      const r = isHighlighted ? (this.pointRadiusValue + 2) : isPareto ? (this.pointRadiusValue + 1.5) : this.pointRadiusValue
      const opacity = isDimmed ? "0.2" : "1"

      const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle")
      circle.setAttribute("cx", String(cx))
      circle.setAttribute("cy", String(cy))
      circle.setAttribute("r", String(r))
      circle.style.fill = fill
      circle.style.opacity = opacity
      if (onFrontier && !hasIcon) {
        circle.style.stroke = this.colors.frontierRing
        circle.style.strokeWidth = "1.4"
      }
      circle.style.cursor = "pointer"
      circle.style.transition = "fill 0.15s, r 0.15s"
      circle.dataset.defaultFill = fill
      circle.dataset.slug = d.point.slug
      if (isPareto) circle.dataset.pareto = "1"

      circle.addEventListener("mouseenter", () => this.showTooltip(d, cx, cy))
      circle.addEventListener("mouseleave", () => this.leaveTooltip())
      circle.addEventListener("touchstart", (e) => { e.preventDefault(); this.showTooltip(d, cx, cy) }, { passive: false })
      circle.addEventListener("click", () => this.selectPoint(circle, d, cx, cy))

      if (hasIcon && d.point.brand_icon_url) {
        // Logo replaces the dot; the circle stays as an invisible hit target.
        const iconSize = r * (onFrontier ? 3 : 2.5)
        circle.style.fill = "transparent"
        circle.style.stroke = "none"
        circle.style.opacity = "0"
        circle.setAttribute("r", String(Math.max(r * 1.65, 9)))
        circle.dataset.branded = "1"
        circle.dataset.iconMarker = "1"

        // Frontier logos get a lime ring so "frontier = lime" still reads.
        if (onFrontier) {
          const ring = document.createElementNS("http://www.w3.org/2000/svg", "circle")
          ring.setAttribute("cx", String(cx))
          ring.setAttribute("cy", String(cy))
          ring.setAttribute("r", String(iconSize / 2 + 2.5))
          ring.setAttribute("fill", "none")
          ring.style.stroke = SELECT_COLOR
          ring.style.strokeWidth = "1.6"
          ring.style.opacity = opacity
          ring.dataset.frontierRing = "1"
          this.svg.appendChild(ring)
        }

        const img = document.createElementNS("http://www.w3.org/2000/svg", "image")
        // Cached data URI embeds the image inline (survives SVG export); falls
        // back to the external href when not yet cached.
        const iconHref = iconDataUriCache.get(d.point.brand_icon_url) || d.point.brand_icon_url
        img.setAttribute("href", iconHref)
        img.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", iconHref)
        img.dataset.slug = d.point.slug
        img.setAttribute("x", String(cx - iconSize / 2))
        img.setAttribute("y", String(cy - iconSize / 2))
        img.setAttribute("width", String(iconSize))
        img.setAttribute("height", String(iconSize))
        img.style.pointerEvents = "none"
        img.style.opacity = opacity
        this.svg.appendChild(img)
        this.iconMap.set(circle, img)
      }

      this.svg.appendChild(circle)
    }
  }

  // Brand-colored square marker for the timeline. Hover shows the tooltip;
  // click pins/unpins it. Kept deliberately simple — no frontier select state.
  private drawTimelineMarker(d: ChartDatum, cx: number, cy: number) {
    const side = this.pointRadiusValue * 2
    const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect")
    rect.setAttribute("x", String(cx - side / 2))
    rect.setAttribute("y", String(cy - side / 2))
    rect.setAttribute("width", String(side))
    rect.setAttribute("height", String(side))
    rect.setAttribute("rx", "0.5")
    rect.style.fill = d.point.brand_color || this.colors.dotMuted
    rect.style.cursor = "pointer"
    rect.dataset.slug = d.point.slug

    rect.addEventListener("mouseenter", () => this.showTooltip(d, cx, cy))
    rect.addEventListener("mouseleave", () => this.leaveTooltip())
    rect.addEventListener("touchstart", (e) => { e.preventDefault(); this.showTooltip(d, cx, cy) }, { passive: false })
    rect.addEventListener("click", () => {
      if (this.pinnedPoint && this.pinnedPoint.d === d) {
        this.pinnedPoint = null
        this.hideTooltip()
      } else {
        this.pinnedPoint = { d, cx, cy }
        this.showTooltip(d, cx, cy)
      }
    })

    // Register the marker as a highlight target so legend hover can fade it. The
    // marker isn't a highlight trigger — its own hover still shows the tooltip.
    if (this.seriesByValue) {
      const series = this.seriesOf(d.point)
      rect.dataset.series = series
      rect.style.transition = "opacity 0.15s ease"
      this.hlMarkers.push({ el: rect, series })
    }

    this.svg.appendChild(rect)
  }

  private selectPoint(circle: SVGCircleElement, d: ChartDatum, cx: number, cy: number) {
    // Deselect previous
    if (this.selectedCircle && this.selectedCircle !== circle) {
      const prevBranded = this.selectedCircle.dataset.branded === "1"
      const prevDefaultRadius = this.defaultMarkerRadius(this.selectedCircle)
      if (!prevBranded) this.selectedCircle.style.fill = this.selectedCircle.dataset.defaultFill || this.colors.dot
      this.selectedCircle.setAttribute("r", String(this.hitRadiusFor(this.selectedCircle, prevDefaultRadius)))
      this.resizeIcon(this.selectedCircle, prevDefaultRadius)
    }
    if (this.selectedLabel) {
      this.selectedLabel.setAttribute("font-size", "8.5px")
      this.selectedLabel.setAttribute("font-weight", "normal")
      this.selectedLabel.style.fill = this.colors.label
    }

    const isBranded = circle.dataset.branded === "1"

    if (this.selectedCircle === circle) {
      // Toggle off
      const defaultRadius = this.defaultMarkerRadius(circle)
      if (!isBranded) circle.style.fill = circle.dataset.defaultFill || this.colors.dot
      circle.setAttribute("r", String(this.hitRadiusFor(circle, defaultRadius)))
      this.resizeIcon(circle, defaultRadius)
      this.selectedCircle = null
      const label = this.labelMap.get(d.point.slug)
      if (label) {
        label.setAttribute("font-size", "8.5px")
        label.setAttribute("font-weight", "normal")
        label.style.fill = this.colors.label
      }
      this.selectedLabel = null
      this.pinnedPoint = null
      this.hideTooltip()
    } else {
      // Select — highlight + bold label
      const selectedRadius = this.pointRadiusValue + 2
      if (!isBranded) circle.style.fill = SELECT_COLOR
      circle.setAttribute("r", String(this.hitRadiusFor(circle, selectedRadius)))
      this.resizeIcon(circle, selectedRadius)
      circle.dataset.slug = d.point.slug
      this.selectedCircle = circle
      const label = this.labelMap.get(d.point.slug)
      if (label) {
        label.setAttribute("font-size", "11px")
        label.setAttribute("font-weight", "700")
        label.style.fill = this.colors.text
      }
      this.selectedLabel = label || null
      this.pinnedPoint = { d, cx, cy }
      this.showTooltip(d, cx, cy)
    }
  }

  private defaultMarkerRadius(circle: SVGCircleElement): number {
    return circle.dataset.pareto === "1" ? this.pointRadiusValue + 1.5 : this.pointRadiusValue
  }

  private hitRadiusFor(circle: SVGCircleElement, visualRadius: number): number {
    return circle.dataset.iconMarker === "1" ? Math.max(visualRadius * 1.65, 9) : visualRadius
  }

  private resizeIcon(circle: SVGCircleElement, radius: number) {
    const img = this.iconMap.get(circle)
    if (!img) return
    const cx = parseFloat(circle.getAttribute("cx")!)
    const cy = parseFloat(circle.getAttribute("cy")!)
    const size = radius * (circle.dataset.pareto === "1" ? 3 : 2.5)
    img.setAttribute("x", String(cx - size / 2))
    img.setAttribute("y", String(cy - size / 2))
    img.setAttribute("width", String(size))
    img.setAttribute("height", String(size))
  }

  private drawLabels(data: ChartDatum[], xScale: LogScale, yScale: LinearScale, paretoSlugs: Set<string>) {
    const FONT_SIZE = this.width >= 760 ? 8.8 : 7.8
    const CHAR_WIDTH = FONT_SIZE * 0.57
    const LABEL_HEIGHT = FONT_SIZE + 4
    const PAD = this.width >= 760 ? 5 : 4
    const PLOT_LEFT = PADDING.left
    const PLOT_RIGHT = PADDING.left + this.plotWidth
    const PLOT_BOTTOM = PADDING.top + this.plotHeight
    const highlightSlug = this.hasHighlightValue ? this.highlightValue : null

    interface LabelBox { x: number; y: number; w: number; h: number; anchorX: number; anchorY: number; textAnchor: string }
    interface PlacedLabel { box: LabelBox; dotCx: number; dotCy: number; text: string; slug: string }

    const dots = data.map(d => {
      const baseRadius = paretoSlugs.has(d.point.slug) ? this.pointRadiusValue + 1.5 : this.pointRadiusValue
      return {
        cx: xScale.toX(d.x),
        cy: yScale.toY(d.y),
        // Logos render much larger than the base dot (plus a frontier ring), so
        // reserve that footprint when keeping labels clear of them.
        r: d.point.brand_icon_url ? baseRadius * 2.0 : baseRadius,
        slug: d.point.slug,
      }
    })

    const placed: LabelBox[] = []
    const placements: PlacedLabel[] = []

    const yHighIsBetter = this.yBestDirectionValue === "high"
    const xLowIsBetter = this.xBestDirectionValue !== "high"
    // Only the Pareto frontier (plus any highlighted model) gets a printed
    // label. Every other model reads as a brand-colored dot / logo and reveals
    // its name on hover, which keeps the plot legible instead of a wall of
    // overlapping text. If the frontier isn't shown, fall back to labeling all.
    const labelable = this.showParetoValue
      ? data.filter(d => paretoSlugs.has(d.point.slug) || d.point.slug === highlightSlug)
      : data
    const sorted = [...labelable].sort((a, b) => {
      const ap = this.labelPriority(a, paretoSlugs, highlightSlug, yHighIsBetter, xLowIsBetter)
      const bp = this.labelPriority(b, paretoSlugs, highlightSlug, yHighIsBetter, xLowIsBetter)
      return bp - ap
    })

    // Distance tiers for candidate positions
    const R1 = this.pointRadiusValue + PAD + 8
    const R2 = R1 + 16
    const R3 = R1 + 34
    const R4 = R1 + 56
    const R5 = R1 + 78
    const R6 = R1 + 104
    const tiers = [R1, R2, R3, R4, R5, R6]

    for (const d of sorted) {
      const isPriority = paretoSlugs.has(d.point.slug) || d.point.slug === highlightSlug

      const cx = xScale.toX(d.x)
      const cy = yScale.toY(d.y)
      const maxChars = isPriority ? (this.width >= 760 ? 24 : 18) : (this.width >= 760 ? 20 : 15)
      const truncName = d.point.name.length > maxChars ? d.point.name.slice(0, maxChars - 1) + "\u2026" : d.point.name
      const labelW = truncName.length * CHAR_WIDTH

      const candidates: LabelBox[] = []

      const addCandidate = (dx: number, dy: number, anchor: string) => {
        let ax: number, bx: number
        if (anchor === "middle") { ax = cx + dx; bx = ax - labelW / 2 }
        else if (anchor === "start") { ax = cx + dx; bx = ax }
        else { ax = cx + dx; bx = ax - labelW } // end
        // Box centered on (cx+dx, cy+dy); text visual center also at cy+dy.
        // SVG text y = baseline = visual center + 0.35 * fontSize (ascent offset).
        let by = cy + dy - LABEL_HEIGHT / 2

        // Use all available chart area without letting labels bleed into axes/export edges.
        bx = Math.max(PLOT_LEFT + 2, Math.min(PLOT_RIGHT - labelW - 2, bx))
        // Allow labels into the top padding (the SVG renders overflow-visible) so a
        // near-ceiling point can still place its label above the dot/logo.
        by = Math.max(4, Math.min(PLOT_BOTTOM - LABEL_HEIGHT - 2, by))

        if (anchor === "middle") ax = bx + labelW / 2
        else if (anchor === "start") ax = bx
        else ax = bx + labelW

        const anchorY = by + LABEL_HEIGHT / 2 + FONT_SIZE * 0.35
        candidates.push({ x: bx, y: by, w: labelW, h: LABEL_HEIGHT, anchorX: ax, anchorY, textAnchor: anchor })
      }

      // 8 directions at each distance tier = 32 candidates
      for (const R of tiers) {
        addCandidate(0, -R - LABEL_HEIGHT / 2, "middle")       // above
        addCandidate(0, R + LABEL_HEIGHT / 2, "middle")         // below
        addCandidate(R, 0, "start")                              // right
        addCandidate(-R, 0, "end")                               // left
        addCandidate(R, -R * 0.7, "start")                      // above-right
        addCandidate(-R, -R * 0.7, "end")                       // above-left
        addCandidate(R, R * 0.7, "start")                        // below-right
        addCandidate(-R, R * 0.7, "end")                         // below-left
      }

      let bestCandidate = candidates[0]
      let bestScore = -Infinity

      for (let ci = 0; ci < candidates.length; ci++) {
        const cand = candidates[ci]
        let score = 50

        // Overlap with placed labels — prefer clean placements, but still place every label.
        let overlapPenalty = 0
        let overlapCount = 0
        for (const p of placed) {
          if (this.boxesOverlap(cand, p, PAD)) {
            overlapCount += 1
            overlapPenalty += this.overlapArea(cand, p, PAD)
          }
        }
        // A flat per-collision penalty dominates the tier/direction bonuses, so
        // a non-overlapping position always wins when one exists (the frontier
        // clusters near the top, where "above" slots clamp onto each other).
        score -= overlapCount * 500 + overlapPenalty * 8

        // Overlap with any data point circle
        for (const dot of dots) {
          if (dot.slug === d.point.slug) continue
          if (dot.cx > cand.x - dot.r - 3 && dot.cx < cand.x + cand.w + dot.r + 3 &&
              dot.cy > cand.y - dot.r - 3 && dot.cy < cand.y + cand.h + dot.r + 3) {
            score -= 30
          }
        }

        // Keep the label visually tied to its dot without making long leader-line spaghetti.
        const centerX = cand.x + cand.w / 2
        const centerY = cand.y + cand.h / 2
        const distance = Math.sqrt((centerX - cx) ** 2 + (centerY - cy) ** 2)
        score -= distance * 0.05

        // Strongly prefer sitting ABOVE the dot/logo: reward labels whose center
        // is above the point, penalise ones that fall below it.
        score += (cy - centerY) * 1.2

        // Distance preference: prefer closer tiers
        const tier = Math.floor(ci / 8)
        score += [18, 12, 7, 3, 1, 0][tier] || 0

        // Direction preference within tier: above > right > above-diagonals
        const dir = ci % 8
        if (dir === 0) score += 3  // above
        if (dir === 2) score += 2  // right
        if (dir === 4 || dir === 5) score += 1  // above-right / above-left

        if (score > bestScore) {
          bestScore = score
          bestCandidate = cand
        }
      }

      if (bestScore === -Infinity) bestCandidate = candidates[0]
      placed.push(bestCandidate)
      placements.push({ box: bestCandidate, dotCx: cx, dotCy: cy, text: truncName, slug: d.point.slug })
    }

    // Pass 1: Draw leader lines from dot center toward label center (= text visual center),
    // clipped at the label box edge.
    for (const p of placements) {
      const lcx = p.box.x + p.box.w / 2
      const lcy = p.box.y + p.box.h / 2
      const dist = Math.sqrt((lcx - p.dotCx) ** 2 + (lcy - p.dotCy) ** 2)

      if (dist > this.pointRadiusValue + 3) {
        const end = this.lineBoxEntry(p.dotCx, p.dotCy, lcx, lcy, p.box)
        this.line(p.dotCx, p.dotCy, end.x, end.y, this.colors.leader, 1)
      }
    }

    // Pass 2: Draw label text (on top of leader lines)
    for (const p of placements) {
      const el = this.text(
        p.box.anchorX,
        p.box.anchorY,
        p.text,
        this.colors.label,
        p.box.textAnchor,
        `${FONT_SIZE}px`
      )
      el.style.pointerEvents = "none"
      el.style.transition = "font-size 0.15s, font-weight 0.15s"
      if (paretoSlugs.has(p.slug)) el.setAttribute("font-weight", "650")
      this.labelMap.set(p.slug, el)
    }
  }

  private labelPriority(d: ChartDatum, paretoSlugs: Set<string>, highlightSlug: string | null, yHighIsBetter: boolean, xLowIsBetter: boolean): number {
    let priority = 0
    if (d.point.slug === highlightSlug) priority += 10_000
    if (paretoSlugs.has(d.point.slug)) priority += 5_000
    priority += yHighIsBetter ? d.y : -d.y
    priority += (xLowIsBetter ? -Math.log10(d.x) : Math.log10(d.x)) * 4
    return priority
  }

  private boxesOverlap(a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }, gap: number): boolean {
    return a.x - gap < b.x + b.w && a.x + a.w + gap > b.x &&
           a.y - gap < b.y + b.h && a.y + a.h + gap > b.y
  }

  private overlapArea(a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }, gap: number): number {
    const xOverlap = Math.max(0, Math.min(a.x + a.w + gap, b.x + b.w) - Math.max(a.x - gap, b.x))
    const yOverlap = Math.max(0, Math.min(a.y + a.h + gap, b.y + b.h) - Math.max(a.y - gap, b.y))
    return xOverlap * yOverlap
  }

  /** Find where the line from (fromX,fromY) to (toX,toY) enters the box (toX,toY is inside). */
  private lineBoxEntry(fromX: number, fromY: number, toX: number, toY: number, box: { x: number; y: number; w: number; h: number }): { x: number; y: number } {
    const dx = toX - fromX
    const dy = toY - fromY
    let tMax = 0

    const edges: number[] = []
    if (dx !== 0) { edges.push((box.x - fromX) / dx, (box.x + box.w - fromX) / dx) }
    if (dy !== 0) { edges.push((box.y - fromY) / dy, (box.y + box.h - fromY) / dy) }

    for (const t of edges) {
      if (t > tMax && t < 1) {
        const ix = fromX + t * dx
        const iy = fromY + t * dy
        if (ix >= box.x - 0.5 && ix <= box.x + box.w + 0.5 && iy >= box.y - 0.5 && iy <= box.y + box.h + 0.5) {
          tMax = t
        }
      }
    }

    return { x: fromX + tMax * dx, y: fromY + tMax * dy }
  }

  private showTooltip(d: ChartDatum, svgX: number, svgY: number) {
    const rect = this.svg.getBoundingClientRect()
    const scaleX = rect.width / this.width
    const scaleY = rect.height / this.height

    const left = svgX * scaleX
    const top = svgY * scaleY - 8

    this.tooltip.style.left = `${left}px`
    this.tooltip.style.top = `${top}px`
    this.tooltip.innerHTML = `
      <div class="font-semibold mb-0.5">${this.escapeHtml(d.point.name)}</div>
      ${d.point.creator ? `<div class="text-base-content/60 mb-0.5">${this.escapeHtml(d.point.creator)}</div>` : ""}
      <div>${this.yLabelValue}: <strong>${this.formatVal(d.y, this.yFormatValue)}</strong></div>
      <div>${this.xLabelValue}: <strong>${this.formatVal(d.x, this.xFormatValue)}</strong></div>
    `
    this.tooltip.classList.remove("opacity-0")
    this.tooltip.classList.add("opacity-100")
  }

  private hideTooltip() {
    this.tooltip.classList.remove("opacity-100")
    this.tooltip.classList.add("opacity-0")
  }

  // On mouse-out: keep the pinned (clicked) point's tooltip visible; otherwise hide.
  private leaveTooltip() {
    if (this.pinnedPoint) {
      this.showTooltip(this.pinnedPoint.d, this.pinnedPoint.cx, this.pinnedPoint.cy)
    } else {
      this.hideTooltip()
    }
  }

  // --- Hover-to-highlight (timeline) ---

  // Wire each ERB-rendered legend row (inside this controller's element) as a
  // hover trigger for its series. Called once per render, after the markers and
  // lines have been collected.
  private setupLegendHighlight() {
    for (const el of Array.from(this.element.querySelectorAll<HTMLElement>(".bm-tl-legend__item[data-series]"))) {
      const series = el.dataset.series || ""
      this.hlLegend.push({ el, series })
      el.addEventListener("mouseenter", () => this.enterHighlight(series))
      el.addEventListener("mouseleave", () => this.scheduleClear())
    }
  }

  // Isolate the hovered legend row's series: full-strength line + markers for it,
  // faded for the rest, and dim the other legend rows.
  private enterHighlight(series: string) {
    this.cancelClear()
    for (const m of this.hlMarkers) m.el.style.opacity = m.series === series ? "1" : TL_MARKER_FADED
    for (const l of this.hlLines) l.el.style.opacity = l.members.has(series) ? TL_LINE_ACTIVE : TL_LINE_FADED
    for (const g of this.hlLegend) g.el.classList.toggle("is-dimmed", g.series !== series)
  }

  private clearHighlight() {
    for (const m of this.hlMarkers) m.el.style.opacity = "1"
    for (const l of this.hlLines) l.el.style.opacity = TL_LINE_DEFAULT
    for (const g of this.hlLegend) g.el.classList.remove("is-dimmed")
  }

  // Defer the clear one frame so moving the cursor between two parts of the same
  // series (a marker → its line, or marker → legend row) doesn't flash the whole
  // chart back to full strength between the mouseleave and the next mouseenter.
  private scheduleClear() {
    if (this.hlClearRaf) return
    this.hlClearRaf = requestAnimationFrame(() => { this.hlClearRaf = 0; this.clearHighlight() })
  }

  private cancelClear() {
    if (this.hlClearRaf) { cancelAnimationFrame(this.hlClearRaf); this.hlClearRaf = 0 }
  }

  // --- Export / Share ---

  async export() {
    // Reset any active hover-highlight so a mid-hover faded state isn't baked
    // into the exported PNG.
    this.cancelClear()
    this.clearHighlight()

    // Lazily fetch the base64 icon data-URIs on demand (not at page load), so
    // load-time imgproxy CORS errors are avoided and we only pay the cost when
    // the user actually exports a PNG. These are required for CORS-safe inlining.
    // The timeline draws no in-SVG brand logos (its markers are squares), so skip
    // the prefetch entirely there — it would be wasted (and slow) work.
    if (this.connectByValue !== "creator") {
      await this.prefetchIcons()
    }

    const clone = this.svg.cloneNode(true) as SVGSVGElement

    // The live SVG renders icons via their external href (data-URIs aren't
    // prefetched at load anymore). Now that prefetchIcons() has populated the
    // cache, swap each external href on the clone for its cached data-URI so the
    // logos survive SVG-as-image serialization.
    for (const img of Array.from(clone.querySelectorAll("image"))) {
      const href = img.getAttribute("href") || ""
      if (href && !href.startsWith("data:")) {
        const dataUri = iconDataUriCache.get(href)
        if (dataUri) {
          img.setAttribute("href", dataUri)
          img.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", dataUri)
        }
      }
    }

    // Icons that couldn't be fetched (CORS) still have external URLs which
    // won't load in SVG-as-image export context. Remove them and restore the
    // hidden brand-colored dot circles underneath as a fallback.
    const removedIconSlugs = new Set<string>()
    for (const img of Array.from(clone.querySelectorAll("image"))) {
      const href = img.getAttribute("href") || ""
      if (href && !href.startsWith("data:")) {
        const slug = (img as SVGImageElement).dataset.slug
        if (slug) removedIconSlugs.add(slug)
        img.remove()
      }
    }
    for (const circle of Array.from(clone.querySelectorAll("circle"))) {
      const iconMarker = circle.getAttribute("data-icon-marker") === "1"
      const slug = circle.getAttribute("data-slug")
      if (iconMarker && (!slug || !removedIconSlugs.has(slug))) continue

      if (circle.style.fill === "transparent") {
        const brandColor = circle.getAttribute("data-default-fill")
        if (brandColor) {
          circle.style.fill = brandColor
          circle.style.opacity = "1"
          circle.setAttribute("r", String(this.defaultMarkerRadius(circle as SVGCircleElement)))
        }
      }
    }

    // Pre-resolve CSS var / color-mix values to rgb() via a temp HTML element
    const colorMap = this.buildResolvedColorMap()

    // Replace inline styles with resolved color attributes
    this.inlineStyles(clone, colorMap)

    // Boost watermark opacity in export
    const watermark = clone.querySelector("[data-watermark]")
    if (watermark) watermark.setAttribute("opacity", "0.12")

    // Expand viewBox with margin so axis labels/ticks aren't clipped at edges
    const m = 20
    const hasTitle = this.hasExportTitleValue
    const titleH = hasTitle ? 28 : 0

    // Bake the (HTML) legend into the PNG as SVG below the chart, so the
    // downloaded image is self-describing.
    const legendTextColor = colorMap.get(this.colors.text) || "#333"
    const legendEntries = this.readLegendEntries()
    const legend = legendEntries.length
      ? this.buildExportLegendGroup(legendEntries, this.width, this.height + 18, legendTextColor)
      : null
    const legendH = legend ? legend.height + 18 : 0

    const exportW = this.width + m * 2
    const exportH = this.height + m * 2 + titleH + legendH
    clone.setAttribute("viewBox", `${-m} ${-m - titleH} ${exportW} ${exportH}`)

    // Add solid background as first child (covers the expanded area)
    const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect")
    bg.setAttribute("x", String(-m))
    bg.setAttribute("y", String(-m - titleH))
    bg.setAttribute("width", String(exportW))
    bg.setAttribute("height", String(exportH))
    bg.setAttribute("fill", this.resolveColor("--color-base-100"))
    clone.insertBefore(bg, clone.firstChild)

    if (legend) clone.appendChild(legend.g)

    // Render export title at top of image
    if (hasTitle) {
      const titleEl = document.createElementNS("http://www.w3.org/2000/svg", "text")
      titleEl.setAttribute("x", String(this.width / 2))
      titleEl.setAttribute("y", String(-m + 4))
      titleEl.setAttribute("text-anchor", "middle")
      titleEl.setAttribute("font-size", "13px")
      titleEl.setAttribute("font-weight", "600")
      titleEl.setAttribute("font-family", "system-ui, sans-serif")
      titleEl.setAttribute("fill", colorMap.get(this.colors.text) || "#333")
      titleEl.textContent = this.exportTitleValue
      clone.appendChild(titleEl)
    }

    // Serialize to SVG string
    const serializer = new XMLSerializer()
    const svgString = serializer.serializeToString(clone)
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" })
    const url = URL.createObjectURL(svgBlob)

    // Render to canvas at 2x for retina
    const scale = 2
    const canvas = document.createElement("canvas")
    canvas.width = exportW * scale
    canvas.height = exportH * scale
    const ctx = canvas.getContext("2d")!

    const img = new Image()
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      URL.revokeObjectURL(url)

      canvas.toBlob((blob) => {
        if (!blob) return
        const a = document.createElement("a")
        a.href = URL.createObjectURL(blob)
        const title = this.hasExportTitleValue ? this.exportTitleValue : (this.hasChartTitleValue ? this.chartTitleValue : "chart")
        a.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`
        a.click()
        URL.revokeObjectURL(a.href)
      }, "image/png")
    }
    img.src = url
  }

  // Read the HTML legend (swatch color + name per item) so export() and other
  // surfaces can reproduce it. Empty when this chart has no legend.
  private readLegendEntries(): { color: string; name: string }[] {
    const legendEl = this.element.querySelector("[data-scatter-chart-legend]")
    if (!legendEl) return []
    return Array.from(legendEl.querySelectorAll(".bm-tl-legend__item")).map((li) => {
      const swatch = li.querySelector(".bm-tl-legend__swatch") as HTMLElement | null
      const nameEl = li.querySelector(".bm-tl-legend__name")
      return {
        color: swatch?.style.backgroundColor || swatch?.style.background || "#888",
        name: (nameEl?.textContent || "").trim(),
      }
    }).filter((e) => e.name)
  }

  // Lay the legend out as flowing SVG swatch+label items across `width`,
  // wrapping into rows. Returns the group and its total height.
  private buildExportLegendGroup(
    entries: { color: string; name: string }[],
    width: number,
    startY: number,
    textColor: string,
  ): { g: SVGGElement; height: number } {
    const g = document.createElementNS("http://www.w3.org/2000/svg", "g")
    const fontSize = 11
    const sw = 9
    const gapSwatch = 5
    const itemGap = 18
    const rowH = 20
    const charW = fontSize * 0.58

    let x = 0
    let y = startY
    for (const e of entries) {
      const itemW = sw + gapSwatch + e.name.length * charW
      if (x > 0 && x + itemW > width) { x = 0; y += rowH }

      const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect")
      rect.setAttribute("x", String(x))
      rect.setAttribute("y", String(y))
      rect.setAttribute("width", String(sw))
      rect.setAttribute("height", String(sw))
      rect.setAttribute("rx", "1")
      rect.setAttribute("fill", e.color)
      g.appendChild(rect)

      const text = document.createElementNS("http://www.w3.org/2000/svg", "text")
      text.setAttribute("x", String(x + sw + gapSwatch))
      text.setAttribute("y", String(y + sw - 0.5))
      text.setAttribute("font-size", `${fontSize}px`)
      text.setAttribute("font-family", "system-ui, sans-serif")
      text.setAttribute("fill", textColor)
      text.textContent = e.name
      g.appendChild(text)

      x += itemW + itemGap
    }

    return { g, height: (y - startY) + rowH }
  }

  private buildResolvedColorMap(): Map<string, string> {
    const map = new Map<string, string>()
    const el = document.createElement("div")
    el.style.position = "absolute"
    el.style.visibility = "hidden"
    document.body.appendChild(el)

    for (const raw of [
      ...Object.values(this.colors),
      "var(--color-base-content)",
      SELECT_COLOR,
      "oklch(85% 0.15 145)",  // quadrant shading green tint
    ]) {
      el.style.color = raw
      map.set(raw, getComputedStyle(el).color)
    }

    document.body.removeChild(el)
    return map
  }

  private inlineStyles(svgEl: SVGSVGElement, colorMap: Map<string, string>) {
    const walk = (el: Element) => {
      if (!(el instanceof SVGElement) || el instanceof SVGDefsElement) return

      const style = el.style

      if (style.fill) {
        const resolved = colorMap.get(style.fill)
        if (resolved) {
          el.setAttribute("fill", resolved)
          style.fill = ""
        }
      }

      if (style.stroke) {
        const resolved = colorMap.get(style.stroke)
        if (resolved) {
          el.setAttribute("stroke", resolved)
          style.stroke = ""
        }
      }

      if (style.strokeWidth) {
        el.setAttribute("stroke-width", style.strokeWidth)
        style.strokeWidth = ""
      }

      if (el.getAttribute("data-watermark") === "true") {
        el.setAttribute("fill", colorMap.get("var(--color-base-content)") || "")
        el.style.fill = ""
      }

      // Resolve CSS vars in fill attributes (used by quadrant shading)
      const fillAttr = el.getAttribute("fill")
      if (fillAttr) {
        const resolved = colorMap.get(fillAttr)
        if (resolved) el.setAttribute("fill", resolved)
      }

      for (const child of el.children) walk(child)
    }
    walk(svgEl)
  }

  private resolveColor(cssVar: string): string {
    const el = document.createElement("div")
    el.style.position = "absolute"
    el.style.visibility = "hidden"
    document.body.appendChild(el)
    el.style.color = `var(${cssVar})`
    const resolved = getComputedStyle(el).color
    document.body.removeChild(el)
    return resolved
  }

  enlarge() {
    const dialog = document.createElement("dialog")
    dialog.className = "modal"

    const box = document.createElement("div")
    // Near-fullscreen so the enlarged chart is clearly bigger than the inline one.
    box.className = "modal-box max-w-none w-[94vw] p-6"

    // Header row
    const header = document.createElement("div")
    header.className = "flex items-center justify-between mb-4"

    const title = document.createElement("h3")
    title.className = "text-lg font-semibold"
    title.textContent = this.hasChartTitleValue ? this.chartTitleValue : "Chart"
    header.appendChild(title)

    const actions = document.createElement("div")
    actions.className = "flex items-center gap-1"

    // Download button (wired to the inner chart's export)
    const dlBtn = document.createElement("button")
    dlBtn.type = "button"
    dlBtn.className = "btn btn-ghost btn-sm btn-square"
    dlBtn.title = "Download as PNG"
    dlBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 opacity-50"><path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" /><path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" /></svg>`
    dlBtn.setAttribute("data-action", "scatter-chart#export")
    actions.appendChild(dlBtn)

    // Close button
    const closeBtn = document.createElement("button")
    closeBtn.type = "button"
    closeBtn.className = "btn btn-ghost btn-sm btn-square"
    closeBtn.title = "Close"
    closeBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4"><path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" /></svg>`
    closeBtn.addEventListener("click", () => dialog.close())
    actions.appendChild(closeBtn)

    header.appendChild(actions)
    box.appendChild(header)

    // Put controller on box so header buttons (download) can find it via Stimulus action bubbling.
    // Chart renders into the target div below.
    const source = this.element as HTMLElement
    for (const attr of source.attributes) {
      if (attr.name.startsWith("data-scatter-chart-") && attr.name.endsWith("-value")) {
        box.setAttribute(attr.name, attr.value)
      }
    }
    box.setAttribute("data-controller", "scatter-chart")

    // Chart on the left, a clone of the (HTML) legend on the right when present,
    // so the enlarged view is as self-describing as the inline one.
    const bodyEl = document.createElement("div")
    bodyEl.className = "flex flex-col lg:flex-row gap-4 lg:gap-6 items-start"

    const chartContainer = document.createElement("div")
    chartContainer.className = "min-w-0 w-full flex-1"
    chartContainer.setAttribute("data-scatter-chart-target", "chart")
    bodyEl.appendChild(chartContainer)

    const legendEl = this.element.querySelector("[data-scatter-chart-legend]")
    if (legendEl) {
      const legendClone = legendEl.cloneNode(true) as HTMLElement
      legendClone.style.flex = "0 0 13rem"
      bodyEl.appendChild(legendClone)
    }

    box.appendChild(bodyEl)

    dialog.appendChild(box)

    // Backdrop click closes dialog
    const backdrop = document.createElement("form")
    backdrop.method = "dialog"
    backdrop.className = "modal-backdrop"
    const backdropBtn = document.createElement("button")
    backdropBtn.textContent = "close"
    backdrop.appendChild(backdropBtn)
    dialog.appendChild(backdrop)

    // Cleanup on close
    dialog.addEventListener("close", () => dialog.remove())

    document.body.appendChild(dialog)
    dialog.showModal()
  }

  shareX() {
    const url = encodeURIComponent(window.location.href)
    const text = encodeURIComponent(this.shareTitle())
    window.open(`https://x.com/intent/tweet?url=${url}&text=${text}`, "_blank", "noopener")
  }

  shareReddit() {
    const url = encodeURIComponent(window.location.href)
    const title = encodeURIComponent(this.shareTitle())
    window.open(`https://reddit.com/submit?url=${url}&title=${title}`, "_blank", "noopener")
  }

  shareLinkedIn() {
    const url = encodeURIComponent(window.location.href)
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, "_blank", "noopener")
  }

  private shareTitle(): string {
    if (this.hasChartTitleValue) return this.chartTitleValue
    return document.title
  }

  private formatVal(v: number, fmt: string): string {
    switch (fmt) {
      case "currency":
        return v >= 1 ? `$${v.toFixed(2)}` : v >= 0.01 ? `$${v.toFixed(3)}` : `$${v.toFixed(4)}`
      case "ms":
        return v >= 1000 ? `${(v / 1000).toFixed(1)}s` : `${Math.round(v)}ms`
      case "number":
        return v >= 100 ? Math.round(v).toLocaleString() : v.toFixed(1)
      case "date": {
        const d = new Date(v)
        if (d.getUTCMonth() === 0 && d.getUTCDate() === 1) return String(d.getUTCFullYear())
        return `${MONTHS[d.getUTCMonth()]} '${String(d.getUTCFullYear()).slice(2)}`
      }
      default:
        return String(v)
    }
  }

  // SVG helpers — use style properties (not attributes) so color-mix() with CSS vars works
  private line(x1: number, y1: number, x2: number, y2: number, stroke: string, width: number, dash?: string): SVGLineElement {
    const el = document.createElementNS("http://www.w3.org/2000/svg", "line")
    el.setAttribute("x1", String(x1))
    el.setAttribute("y1", String(y1))
    el.setAttribute("x2", String(x2))
    el.setAttribute("y2", String(y2))
    el.style.stroke = stroke
    el.style.strokeWidth = String(width)
    if (dash) el.setAttribute("stroke-dasharray", dash)
    this.svg.appendChild(el)
    return el
  }

  private text(x: number, y: number, content: string, fill: string, anchor: string, size: string): SVGTextElement {
    const el = document.createElementNS("http://www.w3.org/2000/svg", "text")
    el.setAttribute("x", String(x))
    el.setAttribute("y", String(y))
    el.style.fill = fill
    el.setAttribute("text-anchor", anchor)
    el.setAttribute("font-size", size)
    el.setAttribute("font-family", "system-ui, sans-serif")
    el.textContent = content
    this.svg.appendChild(el)
    return el
  }

  private escapeHtml(s: string): string {
    const div = document.createElement("div")
    div.textContent = s
    return div.innerHTML
  }
}
